import { TYPES } from "../../types";
// import { api } from "../../api";
import { store } from "redux/store";
import { EChatType, EMessageViewType, 
  IImage
 } from "../chatInterface";
import { EventType } from "hooks/services/ChatServices/EventTypes";
import * as EventHandlers from "hooks/services/ChatServices/EventHandler";

// Define proper TypeScript interfaces
interface IEvent {
  type: string;
  data: string;
}
export interface IGenerateImageBody {
  message: string;
  chatId: number | null;
  newChatBoolean: boolean;
  model: string;
  chatType: EChatType;
  imageCount: number;
  size: string;
  messageViewType: EMessageViewType;
  isPrivate?: boolean;
  regenerate?: boolean;
  folder_id?: number | null;
}

export interface IUpscaleImageBody {
  messageViewType: EMessageViewType;
  message_id: number;
  chat_id: number;
  image_id: number;
}

// export const generateImage = ({
//   messageViewType,
//   ...data
// }: IGenerateImageBody) => {
//   return new Promise((resolve, reject) => {

//     api
//       .post("/api/image/generate", data)
//       .then((res) => {
//         resolve(res.data);
//       })
//       .catch((err) => {
//         if (err?.data?.message === "Network Error") {
//           reject(err);
//         } else {
//           reject(err);
//           store.dispatch({
//             type: TYPES.GENERATE_IMAGE_ERROR,
//             payload: {
//               message: err?.data?.message,
//               messageViewType: messageViewType,
//             },
//           });
//         }
//       });
//   });
// };

// Helper function to extract complete events from the buffer
const extractCompleteEvents = (buffer: string): IEvent[] => {
  const events: IEvent[] = [];
  const lines = buffer.split('\n');
  
  let currentEvent: IEvent = { type: '', data: '' };
  
  for (const line of lines) {
    if (line.startsWith('event: ')) {
      currentEvent.type = line.substring(7);
    } else if (line.startsWith('data: ')) {
      currentEvent.data = line.substring(6);
    } else if (line === '') {
      // Empty line indicates end of an event
      if (currentEvent.type && currentEvent.data) {
        events.push({ ...currentEvent });
        currentEvent = { type: '', data: '' };
      }
    }
  }
  
  return events;
};

// Helper function to remove processed events from the buffer
const removeProcessedEvents = (buffer: string, processedEvents: IEvent[]): string => {
  if (processedEvents.length === 0) return buffer;
  
  let updatedBuffer = buffer;
  
  // Identify and remove processed events from the buffer
  for (const event of processedEvents) {
    const eventText = `event: ${event.type}\ndata: ${event.data}\n\n`;
    const index = updatedBuffer.indexOf(eventText);
    
    if (index !== -1) {
      updatedBuffer = updatedBuffer.substring(index + eventText.length);
    }
  }
  
  return updatedBuffer;
};

// Custom handler for upscale progress to avoid EventHandler compatibility issues
// const handleUpscaleProgress = (data: string, messageId: number, imageId: number): void => {
//   store.dispatch({
//     type: TYPES.UPDATE_UPSCALE_PROGRESS,
//     payload: {
//       messageId,
//       imageId,
//       status: data
//     }
//   });
// };

// Custom handler for upscale completion
const handleUpscaleCompleted = (data: string, messageViewType: string, messageId: number, imageId: number) => {
  try {
    const resultData = JSON.parse(data);
    
    // Create the image object in the expected format
    const image: IImage = {
      ...resultData.image,
      upscaled_images: [],
    };
    
    // Update the store
    store.dispatch({
      type: TYPES.UPSCALE_IMAGE,
      payload: {
        messageViewType,
        message_id: messageId,
        image,
        upscaledImage: {
          image_id: imageId,
          upscale_image_id: resultData.image.id,
        },
      },
    });
    
    return resultData;
  } catch (error) {
    console.error('Error handling complete event:', error);
    throw error;
  }
};

// Updated upscaleImage function using fetch with streaming
export const upscaleImage = ({
  messageViewType,
  message_id,
  chat_id,
  image_id,
  signal,
}: IUpscaleImageBody & { signal?: AbortSignal }): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      // Set initial progress status
      // handleUpscaleProgress("Preparing to upscale...", message_id, image_id);
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASEURL}/api/image/upscale`,
        {
          method: "POST",
          headers: {
            Accept: "text/event-stream",
            "Content-Type": "application/json",
            Authorization: `Bearer ${store.getState().authReducer.userDetail?.token}`,
          },
          body: JSON.stringify({ 
            chat_id, 
            image_id 
          }),
          signal: signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw errorData;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("Failed to get reader from response");
      }
      
      const decoder = new TextDecoder();
      
      let streamBuffer = '';
      let resultData: any = null;
      
      while (true) {
        const streamResult = await reader.read();
        
        if (!streamResult?.value) {
          if (streamResult?.done) {
            // Process any remaining buffer before resolving
            if (streamBuffer.trim()) {
              const completeEvents = extractCompleteEvents(streamBuffer);
              for (const { type, data } of completeEvents) {
                if (type === EventType.COMPLETED) {
                  resultData = handleUpscaleCompleted(
                    data, 
                    messageViewType, 
                    message_id, 
                    image_id
                  );
                }
              }
            }
            
            if (resultData) {
              resolve(resultData);
            } else {
              // If we didn't get complete event but stream is done
              reject({ data: { message: "Upscaling process did not complete properly" } });
            }
            break;
          }
          continue;
        }
        
        const decodedChunk = decoder.decode(streamResult.value, { stream: !streamResult.done });
        streamBuffer += decodedChunk;
        
        // Find complete events in the buffer
        const completeEvents = extractCompleteEvents(streamBuffer);
        
        // Process the complete events
        for (const { type, data } of completeEvents) {
          // Process based on event type
          switch (type) {

            case EventType.CREDITS_UPDATED:
              const userDetail= store.getState().authReducer.userDetail || null;
              EventHandlers.handleCreditsUpdated(data, userDetail );
              break;
              
            case EventType.UPSCALING_STARTED:
            case EventType.IN_PROGRESS:
              break;
              
            case EventType.COMPLETED:
              // Use our custom completed handler
              resultData = handleUpscaleCompleted(data, messageViewType, message_id, image_id);
              break;
              
            case EventType.ERROR:
              // Extract error message and format
              let errorMessage = data;
              if (data.includes(':')) {
                const [, message] = data.split(':', 2);
                errorMessage = message.trim();
              }
              // Reject with properly formatted error
              reader.cancel(); // Cancel the stream
              reject({ data: { message: errorMessage } });
              return; // Exit immediately on error
              
            default:
              console.log('Unhandled event type:', type, data);
          }
        }
        
        // Update the buffer to remove processed events
        streamBuffer = removeProcessedEvents(streamBuffer, completeEvents);
      }
    } catch (error) {
      console.error('Error upscaling image:', error);
      reject(error);
    }
  });
};

// export const upscaleImage = (data: IUpscaleImageBody) => {
//   return new Promise((resolve, reject) => {
//     api
//       .post("/api/image/upscale", {
//         chat_id: data.chat_id,
//         image_id: data.image_id,
//       })
//       .then((res) => {
//         resolve(res.data);
//         const image: IImage = {
//           ...res.data.image,
//           upscaled_images: [],
//         };
//         store.dispatch({
//           type: TYPES.UPSCALE_IMAGE,
//           payload: {
//             messageViewType: data.messageViewType,
//             message_id: data.message_id,
//             image: image,
//             upscaledImage: {
//               image_id: data.image_id,
//               upscale_image_id: res.data.image.id,
//             },
//           },
//         });
//       })
//       .catch((err) => {
//         reject(err);
//       });
//   });
// };

export const setShowUpscaleModal = ({
  showUpscaleModal,
}: {
  showUpscaleModal: "show" | "hide";
}) => {
  store.dispatch({
    type: TYPES.SET_SHOW_UPSCALE_MODAL,
    payload: { showUpscaleModal },
  });
};
